import { addressStore, type CityListDTO } from '@entities/address'
import { useCallback, useMemo } from 'react'
import { useAction, useAppSelector } from '@shared/lib/redux'

export const useGetCityList = () => {
  const state = useAppSelector(addressStore.selectors.selectStatus)
  const getCityList = useAction(addressStore.actions.getCityListStart)
  return {
    getCityList: useCallback(
      (params: CityListDTO) => getCityList(params),
      [getCityList]
    ),
    ...state
  }
}

export const useFetchNextCityPage = () => {
  const canLoad = useAppSelector(addressStore.selectors.selectCanLoadCityMore)
  const currentPage = useAppSelector(addressStore.selectors.selectLastCityPage)
  const load = useAction(addressStore.actions.getCityListStart)
  const { status, error } = useAppSelector(
    addressStore.selectors.selectCityList
  )

  const fetchNextPage = useMemo(
    () =>
      canLoad
        ? (namePart: string) =>
            load({ page: currentPage + 1, cityPart: namePart })
        : () => {},
    [canLoad, currentPage, load]
  )

  return {
    fetchNextPage: useCallback(
      (namePart: string) => fetchNextPage(namePart),
      [fetchNextPage]
    ),
    status,
    error
  }
}

export const useGetAddressList = () => {
  const state = useAppSelector(addressStore.selectors.selectStatus)
  const getAddressList = useAction(addressStore.actions.getAddressList)
  return {
    getAddressList,
    ...state
  }
}

export const useLoadMoreAddresses = () => {
  const state = useAppSelector(addressStore.selectors.selectStatus)
  const getAddressList = useAction(addressStore.actions.loadMoreAddresses)
  return {
    getAddressList,
    ...state
  }
}

export const useFetchNextAddressPage = () => {
  const canLoad = useAppSelector(
    addressStore.selectors.selectCanLoadAddressesMore
  )
  const currentPage = useAppSelector(
    addressStore.selectors.selectLastAddressPage
  )
  const load = useAction(addressStore.actions.getAddressList)
  const { status, error } = useAppSelector(
    addressStore.selectors.selectAddressList
  )
  const cityObject = useAppSelector(addressStore.selectors.selectCurrentCity)

  const fetchNextPage = canLoad
    ? (namePart: string) =>
        load({
          page: currentPage + 1,
          addressPart: namePart,
          city: cityObject?.city ?? ''
        })
    : null

  return {
    fetchNextPage,
    status,
    error
  }
}

export const useSaveCity = () => {
  const saveCity = useAction(addressStore.actions.setCurrentCity)
  return {
    saveCity
  }
}

export const useSetCurrentAddress = () => {
  const setCurrentAddress = useAction(addressStore.actions.setCurrentAddress)
  return setCurrentAddress
}
